import React from 'react';

const C = {
  bg:'#08090f', card:'#0d1117', inp:'#0f1420',
  border:'#1f2937', borderS:'#1a2035',
  primary:'#e5e7eb', secondary:'#6b7280', muted:'#374151',
  profit:'#16C784', loss:'#ef4444', accent:'#6366f1',
};

function SDiv({ label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, margin:'4px 0' }}>
      <div style={{ flex:1, height:1, background:C.borderS }}/>
      <span style={{ color:C.muted, fontSize:9, fontWeight:800, letterSpacing:2, textTransform:'uppercase' }}>{label}</span>
      <div style={{ flex:1, height:1, background:C.borderS }}/>
    </div>
  );
}

function SCard({ children, accent, style={} }) {
  return (
    <div style={{
      borderRadius:12, padding:'10px 12px', boxSizing:'border-box',
      background: accent ? `linear-gradient(135deg,${accent}12 0%,${C.card} 100%)` : C.card,
      border:`1px solid ${accent ? `${accent}28` : C.border}`, ...style,
    }}>
      {children}
    </div>
  );
}

function SHead({ emoji, title, color }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
      <div style={{ width:20, height:20, borderRadius:6, background:`${color||C.accent}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:11 }}>
        {emoji}
      </div>
      <span style={{ color:C.primary, fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:1 }}>{title}</span>
    </div>
  );
}

function MC({ label, value, color }) {
  return (
    <div style={{ padding:'5px 8px', borderRadius:8, background:color?`${color}10`:C.inp, border:`1px solid ${color?`${color}22`:C.border}` }}>
      <div style={{ color:C.secondary, fontSize:8, fontWeight:600, marginBottom:1 }}>{label}</div>
      <div style={{ color:color||C.primary, fontSize:11, fontWeight:800, lineHeight:1.2 }}>{value??'—'}</div>
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{ display:'inline-block', background:`${color}18`, color, border:`1px solid ${color}28`, borderRadius:20, padding:'2px 8px', fontSize:9, fontWeight:700, marginRight:3, marginBottom:3 }}>
      {label}
    </span>
  );
}

function SimulatorShareCard({ snap, cardRef }) {
  if (!snap?.result) return null;

  const { result, day=null, instrument='', direction='buy', holding='intraday' } = snap;
  const pnl       = result.pnl??0;
  const isProfit  = pnl>0;
  const ACC       = isProfit?C.profit:pnl<0?C.loss:'#f59e0b';
  const pnlFmt    = (isProfit?'+':pnl<0?'−':'')+'₹'+Math.abs(Math.round(Number(pnl))).toLocaleString('en-IN');
  const outcomeLabel = result.outcome==='TARGET_HIT'?'Target Hit':result.outcome==='SL_HIT'?'SL Hit':result.outcome==='AMBIGUOUS'?'Ambiguous':'Held to Close';
  const outcomeColor = result.outcome==='TARGET_HIT'?C.profit:result.outcome==='SL_HIT'?C.loss:'#f59e0b';
  const dateStr   = day?.date||(result.exitDate?new Date(result.exitDate).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}):new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}));
  const dirLabel  = direction==='buy'?'LONG':'SHORT';
  const holdLabel = holding==='intraday'?'Intraday':'Carry';
  const instrLabel= result._mode==='equity'?`${snap.stockName||instrument} · Equity`:`Nifty ${instrument} · ${holdLabel}`;

  const entry     = result.entryPrice!=null?parseFloat(result.entryPrice):null;
  const slVal     = result.sl!=null?parseFloat(result.sl):null;
  const tgt       = result.target!=null?parseFloat(result.target):null;
  const exitP     = result.exitPrice!=null?parseFloat(result.exitPrice):null;
  const plannedRR = (entry&&slVal&&tgt)?(Math.abs(tgt-entry)/Math.abs(entry-slVal)).toFixed(1):null;
  const actualR   = (entry&&slVal&&exitP)?((exitP-entry)/Math.abs(entry-slVal)).toFixed(2):null;
  const actualRNum= actualR?parseFloat(actualR):0;
  const actualRCol= actualRNum>=1.5?C.profit:actualRNum>=0?'#f59e0b':C.loss;

  const biasColor  = (b)=>b==='bull'||b==='buy'?C.profit:b==='bear'||b==='sell'?C.loss:C.secondary;
  const biasLabel  = (b)=>b==='bull'||b==='buy'?'↑ Bull':b==='bear'||b==='sell'?'↓ Bear':'─ Neutral';
  const gapBias    = (gt)=>gt?.includes('up')?'bull':gt?.includes('down')?'bear':'neutral';
  const matchColor = day?.conflict?'#f59e0b':day?.match?C.profit:C.loss;
  const matchLabel = day?.conflict?'Conflict':day?.match?'Followed':'Diverged';

  return (
    <div
      ref={cardRef}
      style={{
        width:1080, height:1350, background:C.bg, overflow:'hidden', position:'relative',
        fontFamily:"'Segoe UI',system-ui,-apple-system,sans-serif",
        display:'flex', flexDirection:'column', padding:28, gap:7, boxSizing:'border-box',
      }}
    >
      {/* Ambient glow */}
      <div style={{ position:'absolute', top:-80, right:-80, width:220, height:220, borderRadius:'50%', background:`radial-gradient(circle,${ACC}0e 0%,transparent 65%)`, pointerEvents:'none' }}/>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#22c55e30,#22c55e10)', border:'1px solid #22c55e35', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ color:'#22c55e', fontSize:14, fontWeight:900 }}>T</span>
          </div>
          <span style={{ color:'#fff', fontSize:16, fontWeight:800 }}>TradeReflex</span>
        </div>
        <span style={{ color:C.muted, fontSize:11 }}>{dateStr}</span>
        <span style={{ color:C.muted, fontSize:11 }}>tradereflex.in</span>
      </div>

      {/* Hero */}
      <SCard accent={ACC}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:6 }}>
          <div>
            <span style={{ color:'#fff', fontSize:22, fontWeight:900, lineHeight:1, display:'block', marginBottom:6 }}>{instrLabel}</span>
            <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:6 }}>
              <Pill label={dirLabel}     color={dirLabel==='LONG'?C.profit:C.loss}/>
              <Pill label={holdLabel}    color="#818cf8"/>
              <Pill label={outcomeLabel} color={outcomeColor}/>
              {day && <Pill label={matchLabel} color={matchColor}/>}
            </div>
            <p style={{ color:ACC, fontSize:38, fontWeight:900, margin:0, lineHeight:1, letterSpacing:'-1px' }}>{pnlFmt}</p>
            {result._mode==='equity' && <p style={{ color:ACC, fontSize:11, margin:'2px 0 0', opacity:0.8 }}>per share</p>}
          </div>
          {actualR && (
            <div style={{ textAlign:'right' }}>
              <p style={{ color:C.secondary, fontSize:9, margin:0, letterSpacing:'1px' }}>ACTUAL R</p>
              <p style={{ color:actualRCol, fontSize:22, fontWeight:900, margin:0 }}>{actualRNum>=0?'+':''}{actualR}R</p>
            </div>
          )}
        </div>
      </SCard>

      <SDiv label="Trade Execution"/>

      {/* 2-column: execution + day context */}
      <div style={{ display:'flex', gap:7 }}>
        {/* Left: execution details */}
        <SCard style={{ flex:1 }}>
          <SHead emoji="⚡" title="Execution" color={ACC}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
            <MC label="Entry"     value={entry!=null?`₹${entry}`:'—'} color="#fff"/>
            <MC label="Stop Loss" value={slVal!=null?`₹${slVal}`:'—'} color={C.loss}/>
            <MC label="Target"    value={tgt!=null?`₹${tgt}`:'—'}     color={C.profit}/>
            <MC label="Exit"      value={exitP!=null?`₹${exitP}`:'—'} color={ACC}/>
            {plannedRR && <MC label="Planned R:R" value={`1:${plannedRR}`} color="#818cf8"/>}
            {actualR   && <MC label="Actual R"    value={`${actualR}R`}    color={actualRCol}/>}
            {result.daysHeld!=null && <MC label="Days Held"   value={`${result.daysHeld}d`}/>}
            {result.entryTime && <MC label="Entry Time" value={result.entryTime}/>}
            {result.exitTime  && <MC label="Exit Time"  value={result.exitTime}/>}
          </div>
        </SCard>

        {/* Right: day context */}
        {day && (day.globalBias||day.gapType||day.fiiBias||day.diiBias) && (
          <SCard style={{ flex:1 }}>
            <SHead emoji="📊" title="Day Context" color="#06b6d4"/>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
              {day.globalBias && <MC label="Global"   value={biasLabel(day.globalBias)} color={biasColor(day.globalBias)}/>}
              {day.gapType    && <MC label="Gap"      value={biasLabel(gapBias(day.gapType))} color={biasColor(gapBias(day.gapType))}/>}
              {day.fiiBias    && <MC label="FII"      value={biasLabel(day.fiiBias)}    color={biasColor(day.fiiBias)}/>}
              {day.diiBias    && <MC label="DII"      value={biasLabel(day.diiBias)}    color={biasColor(day.diiBias)}/>}
              {day.niftyChangePct!=null && <MC label="Nifty"   value={`${day.niftyChangePct>=0?'+':''}${day.niftyChangePct}%`} color={day.niftyChangePct>=0?C.profit:C.loss}/>}
              {day.breadth!=null && <MC label="Breadth" value={`${day.breadth}%`} color={day.breadth>=60?C.profit:day.breadth<=40?C.loss:'#f59e0b'}/>}
              {day.fo?.strike && <MC label="ATM Strike" value={day.fo.strike}/>}
            </div>
          </SCard>
        )}
      </div>

      {/* 2-column: Scanners + Sectors */}
      {((day?.scanners&&(day.scanners.bull?.signals>0||day.scanners.bear?.signals>0)) || day?.sectors?.length>0) && (
        <div style={{ display:'flex', gap:7 }}>
          {day?.scanners&&(day.scanners.bull?.signals>0||day.scanners.bear?.signals>0) && (
            <SCard accent="#f59e0b" style={{ flex:1 }}>
              <SHead emoji="📡" title="Scanner Signals" color="#f59e0b"/>
              <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                {day.scanners.bull?.signals>0 && <Pill label={`${day.scanners.bull.signals} Bullish${day.scanners.bull.hit_rate!=null?` · ${day.scanners.bull.hit_rate}% hit`:''}`} color={C.profit}/>}
                {day.scanners.bear?.signals>0 && <Pill label={`${day.scanners.bear.signals} Bearish${day.scanners.bear.hit_rate!=null?` · ${day.scanners.bear.hit_rate}% hit`:''}`} color={C.loss}/>}
              </div>
            </SCard>
          )}
          {day?.sectors?.length>0 && (
            <SCard style={{ flex:1 }}>
              <SHead emoji="🏭" title="Sectors" color="#f59e0b"/>
              <div style={{ display:'flex', flexWrap:'wrap' }}>
                {day.sectors.slice(0,8).map(sec => {
                  const col = sec.avg>=0?C.profit:C.loss;
                  return <Pill key={sec.name} label={`${sec.name} ${sec.avg>=0?'+':''}${sec.avg}%`} color={col}/>;
                })}
              </div>
            </SCard>
          )}
        </div>
      )}

      {day?.verdictSentence && (
        <SCard accent={matchColor}>
          <p style={{ color:C.secondary, fontSize:10, margin:'0 0 4px', fontWeight:700, textTransform:'uppercase', letterSpacing:'1px' }}>Day Verdict</p>
          <p style={{ color:C.primary, fontSize:12, lineHeight:1.55, margin:0, fontStyle:'italic' }}>{day.verdictSentence}</p>
        </SCard>
      )}

      {/* Footer */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:8, borderTop:`1px solid ${C.borderS}`, flexShrink:0, marginTop:'auto' }}>
        <span style={{ color:C.muted, fontSize:10 }}>Know your edge · TradeReflex</span>
        <span style={{ color:C.secondary, fontSize:10, fontWeight:700 }}>tradereflex.in</span>
      </div>
    </div>
  );
}

export default SimulatorShareCard;
